import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { IChecklistV2Viewer, ICheckpoint2AnswerModel } from '@fi-shared/checklist/checklist-v2';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiChecklistActionsHelperService } from '@fi-shared/checklist/fi-checklist-actions-helper.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiChecklist2ReportStateService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-state.service';
import { FiMatIconCheckboxMenuComponent } from '@fi-shared/fi-mat-icon-checkbox-menu/fi-mat-icon-checkbox-menu.component';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

@Component({
    selector: 'fi-checklist2-viewer-menu',
    templateUrl: './viewer-menu.component.html',
    styleUrls: ['./viewer-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-viewer-menu' },
    standalone: false,
})
export class FiChecklist2ViewerMenuComponent implements OnInit {
    @Input() checklistViewer: IChecklistV2Viewer;
    @Input() initialAnswers: ICheckpoint2AnswerModel[] = [];
    @Output() checklistViewerChange = new EventEmitter<IChecklistV2Viewer>();
    @Output() onApplicableChange = new EventEmitter<void>();
    @Output() checklistDelete = new EventEmitter<void>();
    @Input({ required: true }) businessUnitId: number;
    @Input({ required: true }) auditMode: boolean;
    @Input({ required: true }) isPreviewMode: boolean;

    actions: IItemsListAction2<IChecklistV2Viewer>[] = [];
    private toggleNotApplicableAction: IItemsListAction2<IChecklistV2Viewer>;

    @ViewChild(FiMatIconCheckboxMenuComponent) matIconCheckboxMenuComponent: FiMatIconCheckboxMenuComponent<any>;

    constructor(
        private appTextService: AppTextService,
        private fiChecklist2Service: FiChecklist2Service,
        private fiChecklist2TemplateService: FiChecklist2TemplateService,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklistActionsHelperService: FiChecklistActionsHelperService,
        private fiSnackBarService: FiSnackBarService,
        private fiChecklist2ReportStateService: FiChecklist2ReportStateService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        if (this.isPreviewMode) {
            this.setActionAvailableForPreview();
        } else {
            this.setActions();
        }
    }

    private setActionAvailableForPreview() {
        this.actions = [
            this.fiChecklistActionsHelperService.getChecklistEditAction<IChecklistV2Viewer>({
                auditMode: this.auditMode,
                checklistIdFn: () => this.checklistViewer.Checklist.Id,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
            this.fiChecklistActionsHelperService.getCompletionModeAction(this.auditMode),
        ];
    }

    private setActions() {
        this.toggleNotApplicableAction =
            this.fiChecklistActionsHelperService.getChecklistUnitNotApplicableAction<IChecklistV2Viewer>({
                hasAnswersFn: () => !!this.checklistViewer.Answers.find((ans) => isDefined(ans.Value)),
                getChecklistNameFn: () => this.checklistViewer.Checklist.Name,
                getUnitNameFn: () => this.checklistViewer.BusinessUnitName,
                onSuccess: () => {
                    this.checklistViewerChange.emit(this.checklistViewer);
                    this.onApplicableChange.emit();
                    this.matIconCheckboxMenuComponent.detectChanges();
                    this.cdr.markForCheck();
                },
                businessUnitId: this.businessUnitId,
                auditMode: this.auditMode,
            });
        this.actions = [
            this.fiChecklistActionsHelperService.getChecklistEditAction<IChecklistV2Viewer>({
                checklistIdFn: (data) => this.checklistViewer.Checklist.Id,
                auditMode: this.auditMode,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
            this.fiChecklistActionsHelperService.getViewReportAction<IChecklistV2Viewer>({
                onClick: (data) => {
                    this.fiChecklist2ReportStateService.goToReport(
                        this.checklistViewer.Checklist.Id,
                        this.checklistViewer.ScheduleId,
                        this.checklistViewer,
                        this.auditMode,
                    );
                },
                businessUnitId: this.businessUnitId,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getChecklistGlobalMobileNotificationsAction<IChecklistV2Viewer>({
                onSuccessFn: () => {
                    this.checklistViewerChange.emit(this.checklistViewer);
                },
                getChecklistNameFn: () => this.checklistViewer.Checklist.Name,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getChecklistPersonalMobileNotificationsAction<IChecklistV2Viewer>({
                onSuccessFn: () => {
                    this.checklistViewerChange.emit(this.checklistViewer);
                },
                getChecklistNameFn: () => this.checklistViewer.Checklist.Name,
                auditMode: this.auditMode,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistResponsesAction<IChecklistV2Viewer>({
                getChecklistToScheduleIdFn: () => this.checklistViewer.ChecklistToScheduleId,
                getChecklistNameFn: () => this.checklistViewer.Checklist.Name,
                getScheduleNameFn: () => this.checklistViewer.ScheduleName,
                disabled: () => !this.checklistViewer.HasAnswers,
                afterDeleteFn: () => {
                    this.checklistViewer.HasAnswers = false;
                    this.fiSnackBarService.showSnackbar({
                        textMessage: this.appTextService.get('checklist.responses_deleted'),
                    });
                    this.cdr.markForCheck();
                },
                businessUnitId: this.businessUnitId,
                auditMode: this.auditMode,
            }),
            this.toggleNotApplicableAction,
            this.fiChecklistActionsHelperService.getScheduleChecklistAction({
                checklistIdFn: (data) => data.Checklist.Id,
                auditMode: this.auditMode,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistAction<IChecklistV2Viewer>({
                onClick: () => {
                    this.fiChecklistHelperService
                        .getConfirmDeleteChecklistDialog({
                            item: this.checklistViewer,
                            checklistIdFn: () => this.checklistViewer.Checklist.Id,
                            auditMode: this.auditMode,
                        })
                        .then((result) => {
                            if (result.result) {
                                const checklist = this.checklistViewer.Checklist;
                                this.fiChecklist2Service.deleteChecklists([checklist.Id], this.auditMode).then(() => {
                                    const objectType = !this.auditMode
                                        ? ObjectTypes.Checklist2
                                        : ObjectTypes.Checklist2Audit;
                                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                                        objectType,
                                        [checklist.Id],
                                        checklist.Name,
                                    );

                                    this.checklistDelete.emit();
                                });
                            }
                        });
                },
                auditMode: this.auditMode,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
            // template view mode
            this.fiChecklistActionsHelperService.getChecklistTemplateEditAction<IChecklistV2Viewer>({
                checklistTemplateIdFn: (data) => data.Checklist.Id,
                auditMode: this.auditMode,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
            this.fiChecklistActionsHelperService.getCreateChecklistFromTemplateAction<IChecklistV2Viewer>({
                checklistTemplateIdFn: (data) => data.Checklist.Id,
                auditMode: this.auditMode,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
            this.fiChecklistActionsHelperService.getDeleteChecklistTemplateAction<IChecklistV2Viewer>({
                onClick: (data) => {
                    this.fiChecklistHelperService.getConfirmTemplateDeleteDialog().onYesButtonClick(() => {
                        this.fiChecklist2TemplateService
                            .deleteChecklistTemplates([data.Checklist.Id], this.auditMode)
                            .then(() => {
                                this.fiChecklistHelperService.showTemplateDeletedSnackbar(
                                    data.Checklist,
                                    this.auditMode,
                                );
                                this.checklistDelete.emit();
                            });
                    });
                },
                auditMode: this.auditMode,
                templateMode: this.checklistViewer.Checklist.TemplateMode,
            }),
        ];
    }

    isChecklistViewerMenuTriggerShown(): boolean {
        return !!this.actions.find((action) => !action.condition || action.condition(this.checklistViewer));
    }

    hasAccessToToggleApplicable(): boolean {
        return this.toggleNotApplicableAction?.condition(this.checklistViewer);
    }

    toggleChecklistNotApplicable() {
        this.toggleNotApplicableAction.onClick(this.checklistViewer);
    }
}
